/**
 * RestroPulse POS · Theme tokens (Sprint 7 — premium UX)
 * ─────────────────────────────────────────────────────────────────────
 * Single source of truth for POS-specific colour, typography, shape
 * and elevation tokens. Every colour and every meaningful text size
 * referenced by the POS shell (FloorView, BillingScreen, dialogs,
 * slips, reports, tender pills) resolves back to a token defined here.
 *
 * Production intent — tenant branding overrides these tokens at runtime.
 * A tenant's white-labelled deployment ships its own theme override,
 * and this file's defaults are the fallback. The rest of the POS never
 * references a raw hex value or a raw `text-[13px]` for a "money" or
 * "slip" cell — it uses `var(--pos-*)` custom properties instead.
 *
 * Sprint 7:
 *   • Semantic slots widened from 6 → 9 colour slots (primary, selected,
 *     accent, page_bg, card_bg, raised_surface, border, primary_text,
 *     secondary_text).
 *   • Added typography (font_family, heading_font, font_weight_preset),
 *     shape (radius_preset) and elevation (elevation_preset) tokens.
 *   • Shared resolver `resolvePosTheme(effective)` produces the CSS-var
 *     map used identically by the live terminal AND the appearance
 *     preview — no divergent styling logic.
 */

// ─── Colour tokens ─────────────────────────────────────────────────
// Semantic names, not shades. `primary` is the dominant action fill
// (charcoal in the graphite default); `accent` is hover/secondary
// emphasis (brand green); `selected` is a highlight tint; the surface
// tokens are the three-tier depth stack; `border` is silver-lined
// hairline; text tokens are charcoal and graphite.
export const POS_THEME = Object.freeze({
  color: {
    // Legacy names retained for the /pos-demo path and admin tokens.
    primary:            "#1A2421",
    primaryHover:       "#143E30",  // accent used as primary hover
    primaryActive:      "#0B0F0E",
    primaryTint:        "#E7EEEB",
    primaryTintSoft:    "#F4F7F5",
    onPrimary:          "#FFFFFF",

    // Sprint 7 · semantic slots exposed to admins.
    accent:             "#143E30",
    selected:           "#E7EEEB",
    pageBg:             "#F7F8F5",
    cardBg:             "#FFFFFF",
    raisedSurface:      "#FBFBF8",
    border:             "#D1D5D0",
    primaryText:        "#0B0F0E",
    secondaryText:      "#3F4A47",

    // Auxiliary neutrals (system-owned, not admin-controlled).
    secondary:          "#334155",
    secondaryHover:     "#1E293B",
    onSecondary:        "#FFFFFF",

    // Operational status colours — SYSTEM-CONTROLLED. Never exposed to
    // the appearance UI so success/warning/error remain recognisable
    // regardless of a tenant's brand choice.
    destructive:        "#B91C1C",
    destructiveTint:    "#FEF2F2",
    onDestructive:      "#FFFFFF",

    warning:            "#B45309",
    warningTint:        "#FEF3C7",
    onWarning:          "#1A2421",

    success:            "#065F46",
    successTint:        "#ECFDF5",

    // Legacy compatibility — kept so files not yet swept still resolve.
    surface:            "#F7F8F5",
    surfaceElevated:    "#FFFFFF",
    surfaceMuted:       "#FBFBF8",
    borderSoft:         "#E5E7E1",
    text:               "#0B0F0E",
    textMuted:          "#3F4A47",
    textOnDark:         "#FFFFFF",
  },
  type: {
    moneyHero:      { size: "28px", weight: 700, tracking: "-0.01em" },
    money:          { size: "20px", weight: 600, tracking: "-0.005em" },
    moneySmall:     { size: "14px", weight: 600 },
    headingLg:      { size: "16px", weight: 700 },
    heading:        { size: "13px", weight: 600 },
    body:           { size: "13px", weight: 400 },
    bodySmall:      { size: "12px", weight: 400 },
    label:          { size: "10px", weight: 600, tracking: "0.08em", transform: "uppercase" },
    labelSmall:     { size: "9px",  weight: 600, tracking: "0.08em", transform: "uppercase" },
    slipHeading:    { size: "14px", weight: 700, family: "monospace" },
    slipBody:       { size: "12px", weight: 500, family: "monospace" },
    slipMuted:      { size: "10px", weight: 500, family: "monospace" },
  },
});

// ─── Preset resolvers ────────────────────────────────────────────────
// Radius preset → concrete radii for the three tier sizes we use in
// the terminal: 4/8/12 (small controls) → 6/10/14 (default) → 10/14/18
// (rounded). Each preset is applied to `--pos-radius-*` variables.
const RADIUS_MAP = {
  sharp:   { sm: "2px",  md: "4px",  lg: "6px",  xl: "8px"  },
  soft:    { sm: "6px",  md: "10px", lg: "14px", xl: "18px" },
  rounded: { sm: "10px", md: "14px", lg: "18px", xl: "24px" },
};

// Elevation preset → three-tier shadow stack. Flat has no shadow but
// keeps a hairline border to preserve card edges; premium adds a
// glass-like inner highlight for the funding-demo aesthetic.
const ELEVATION_MAP = {
  flat: {
    sm: "0 0 0 1px rgba(11,15,14,0.04)",
    md: "0 0 0 1px rgba(11,15,14,0.06)",
    lg: "0 0 0 1px rgba(11,15,14,0.08)",
    inner: "none",
  },
  soft: {
    sm: "0 1px 2px rgba(11,15,14,0.05), 0 1px 1px rgba(11,15,14,0.04)",
    md: "0 2px 4px rgba(11,15,14,0.05), 0 4px 12px rgba(11,15,14,0.06)",
    lg: "0 4px 8px rgba(11,15,14,0.06), 0 12px 32px rgba(11,15,14,0.08)",
    inner: "inset 0 1px 0 rgba(255,255,255,0.6)",
  },
  premium: {
    sm: "0 1px 2px rgba(11,15,14,0.06), 0 2px 4px rgba(11,15,14,0.05), inset 0 1px 0 rgba(255,255,255,0.7)",
    md: "0 2px 6px rgba(11,15,14,0.06), 0 8px 20px rgba(11,15,14,0.08), inset 0 1px 0 rgba(255,255,255,0.7)",
    lg: "0 6px 14px rgba(11,15,14,0.08), 0 18px 48px rgba(11,15,14,0.10), inset 0 1px 0 rgba(255,255,255,0.8)",
    inner: "inset 0 1px 0 rgba(255,255,255,0.9), inset 0 0 0 1px rgba(255,255,255,0.35)",
  },
};

// Font-weight preset → concrete Tailwind-compatible numeric weights
// applied to `--pos-font-weight-*` variables so components can pick
// the right weight without hardcoding.
const WEIGHT_MAP = {
  regular:  { base: 400, medium: 500, bold: 600 },
  medium:   { base: 450, medium: 550, bold: 650 },
  semibold: { base: 500, medium: 600, bold: 700 },
};

// Font-family fallback stack. `System UI` maps to the platform system
// stack; the rest are Google-hosted webfonts loaded once by
// `ensurePosFontLoaded`.
const FONT_STACKS = {
  "Inter":               "'Inter', system-ui, -apple-system, sans-serif",
  "Manrope":             "'Manrope', system-ui, -apple-system, sans-serif",
  "Plus Jakarta Sans":   "'Plus Jakarta Sans', system-ui, -apple-system, sans-serif",
  "IBM Plex Sans":       "'IBM Plex Sans', system-ui, -apple-system, sans-serif",
  "Source Sans 3":       "'Source Sans 3', 'Source Sans Pro', system-ui, sans-serif",
  "DM Sans":             "'DM Sans', system-ui, -apple-system, sans-serif",
  "System UI":           "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
};

const GOOGLE_FONT_HREFS = {
  "Inter":               "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap",
  "Manrope":             "https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700&display=swap",
  "Plus Jakarta Sans":   "https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap",
  "IBM Plex Sans":       "https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600;700&display=swap",
  "Source Sans 3":       "https://fonts.googleapis.com/css2?family=Source+Sans+3:wght@400;500;600;700&display=swap",
  "DM Sans":             "https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap",
};

const _loadedFonts = new Set();

/** Idempotently inject a Google Fonts <link> for the given family. */
export function ensurePosFontLoaded(family) {
  if (typeof document === "undefined") return;
  if (!family || _loadedFonts.has(family)) return;
  const href = GOOGLE_FONT_HREFS[family];
  if (!href) { _loadedFonts.add(family); return; }
  const existing = document.querySelector(`link[data-pos-font="${family}"]`);
  if (existing) { _loadedFonts.add(family); return; }
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = href;
  link.setAttribute("data-pos-font", family);
  document.head.appendChild(link);
  _loadedFonts.add(family);
}

// ─── WCAG contrast helpers (mirror the server) ───────────────────────
function _hexToRgb(hex) {
  const h = String(hex || "").trim().replace("#", "");
  const long = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  return [
    parseInt(long.slice(0, 2), 16) || 0,
    parseInt(long.slice(2, 4), 16) || 0,
    parseInt(long.slice(4, 6), 16) || 0,
  ];
}

function _luminance(hex) {
  const [r, g, b] = _hexToRgb(hex);
  const f = (c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b);
}

export function contrastRatio(a, b) {
  const la = _luminance(a), lb = _luminance(b);
  const hi = Math.max(la, lb), lo = Math.min(la, lb);
  return (hi + 0.05) / (lo + 0.05);
}

/**
 * Return the higher-contrast text colour for a given background.
 * Dark charcoal (#0B0F0E) beats pure white on any light or medium fill.
 * Never rejects a light fill — always picks the appropriate text.
 */
export function bestTextOn(background) {
  const dark = "#0B0F0E";
  const white = "#FFFFFF";
  return contrastRatio(dark, background) >= contrastRatio(white, background) ? dark : white;
}

// ─── Preset accessors ────────────────────────────────────────────────
export function radiiFor(preset)     { return RADIUS_MAP[preset] || RADIUS_MAP.soft; }
export function elevationFor(preset) { return ELEVATION_MAP[preset] || ELEVATION_MAP.soft; }
export function weightsFor(preset)   { return WEIGHT_MAP[preset]   || WEIGHT_MAP.medium; }
export function fontStackFor(family) { return FONT_STACKS[family]  || FONT_STACKS.Inter; }

/**
 * The shared theme-token resolver used identically by the live
 * terminal AND the appearance preview. Given the effective theme
 * (the server-resolved cascade default → tenant → outlet), returns
 * a flat map of CSS variables to write onto a scoped root element.
 *
 * The returned map is deterministic — same input always yields the
 * same map — so the preview can diff-render safely.
 */
export function resolvePosTheme(effective) {
  const eff = effective || {};
  const primary        = eff.primary        || POS_THEME.color.primary;
  const selected       = eff.selected       || POS_THEME.color.selected;
  const accent         = eff.accent         || POS_THEME.color.accent;
  const pageBg         = eff.page_bg        || eff.pageBg        || eff.workspace_bg || POS_THEME.color.pageBg;
  const cardBg         = eff.card_bg        || eff.cardBg        || eff.panel_bg     || POS_THEME.color.cardBg;
  const raisedSurface  = eff.raised_surface || eff.raisedSurface || POS_THEME.color.raisedSurface;
  const border         = eff.border         || POS_THEME.color.border;
  const primaryText    = eff.primary_text   || eff.primaryText   || POS_THEME.color.primaryText;
  const secondaryText  = eff.secondary_text || eff.secondaryText || POS_THEME.color.secondaryText;

  const onPrimary  = eff.on_primary  || bestTextOn(primary);
  const onSelected = eff.on_selected || bestTextOn(selected);
  const onAccent   = eff.on_accent   || bestTextOn(accent);

  const fontFamily    = eff.font_family        || eff.fontFamily   || "Inter";
  const headingFont   = eff.heading_font       || eff.headingFont  || fontFamily;
  const weightPreset  = eff.font_weight_preset || eff.fontWeightPreset || "medium";
  const radiusPreset  = eff.radius_preset      || eff.radiusPreset  || "soft";
  const elevPreset    = eff.elevation_preset   || eff.elevationPreset || "soft";

  const radii   = radiiFor(radiusPreset);
  const shadows = elevationFor(elevPreset);
  const weights = weightsFor(weightPreset);
  const fontStack = fontStackFor(fontFamily);
  const headingStack = fontStackFor(headingFont);

  return {
    // ─ colour ────────────────────────────────────────────────────────
    "--pos-color-primary":         primary,
    "--pos-color-primary-hover":   accent,      // hover uses accent
    "--pos-color-primary-active":  primary,
    "--pos-color-primary-tint":    selected,
    "--pos-color-on-primary":      onPrimary,

    "--pos-color-selected":        selected,
    "--pos-color-on-selected":     onSelected,

    "--pos-color-accent":          accent,
    "--pos-color-on-accent":       onAccent,

    "--pos-color-page-bg":         pageBg,
    "--pos-color-card-bg":         cardBg,
    "--pos-color-raised-surface":  raisedSurface,

    "--pos-color-border":          border,
    "--pos-color-border-soft":     border,

    "--pos-color-text":            primaryText,
    "--pos-color-text-muted":      secondaryText,
    "--pos-color-primary-text":    primaryText,
    "--pos-color-secondary-text":  secondaryText,

    // ─ typography ───────────────────────────────────────────────────
    "--pos-font-family":           fontStack,
    "--pos-font-heading":          headingStack,
    "--pos-font-weight-base":      String(weights.base),
    "--pos-font-weight-medium":    String(weights.medium),
    "--pos-font-weight-bold":      String(weights.bold),

    // ─ shape ────────────────────────────────────────────────────────
    "--pos-radius-sm":             radii.sm,
    "--pos-radius-md":             radii.md,
    "--pos-radius-lg":             radii.lg,
    "--pos-radius-xl":             radii.xl,

    // ─ elevation ────────────────────────────────────────────────────
    "--pos-shadow-sm":             shadows.sm,
    "--pos-shadow-md":             shadows.md,
    "--pos-shadow-lg":             shadows.lg,
    "--pos-shadow-inner":          shadows.inner,

    // ─ legacy admin-token bridge (backwards compat) ─────────────────
    "--color-primary":             primary,
    "--color-primary-hover":       accent,
    "--color-primary-active":      primary,

    // ─ legacy pos-terminal-* namespace (still referenced by a handful
    //   of files not yet swept) ──────────────────────────────────────
    "--pos-terminal-workspace":    pageBg,
    "--pos-terminal-panel":        cardBg,
    "--pos-terminal-primary":      primary,
    "--pos-terminal-on-primary":   onPrimary,
    "--pos-terminal-selected":     selected,
    "--pos-terminal-accent":       accent,
    "--pos-terminal-border":       border,
  };
}

/**
 * Write every CSS variable produced by `resolvePosTheme` onto the
 * given scoped root element (or :root when omitted). Used by BOTH the
 * live terminal (POSTerminal.js) and the appearance preview
 * (PosAppearanceSection.js) so there is exactly one styling code path.
 *
 * Also injects the Google Fonts <link> tag once per family and applies
 * `font-family` directly onto the root so descendants inherit.
 */
export function applyPosThemeVars(vars, root = null) {
  if (typeof document === "undefined") return;
  const target = root || document.documentElement;
  Object.entries(vars || {}).forEach(([k, v]) => {
    if (v == null) return;
    target.style.setProperty(k, v);
  });
  // Directly set font-family so it inherits without every leaf needing
  // to reference `var(--pos-font-family)`.
  const family = vars["--pos-font-family"];
  if (family) target.style.fontFamily = family;
}

/**
 * Legacy shim — still called by POSTerminal.js's early paint. Given an
 * object shaped like `POS_THEME` (with a nested `color` map), computes
 * the CSS var map and writes it. Retained so we do not have to sweep
 * every early-paint caller in this sprint.
 */
export function applyPosTheme(theme = POS_THEME, root = null) {
  const c = (theme && theme.color) || POS_THEME.color;
  // Compose an effective-shape object from the legacy color map so
  // the shared resolver can process it uniformly.
  const effective = {
    primary:         c.primary,
    selected:        c.selected || c.primaryTint,
    accent:          c.accent   || c.primaryHover,
    page_bg:         c.pageBg   || c.surface,
    card_bg:         c.cardBg   || c.surfaceElevated,
    raised_surface:  c.raisedSurface || c.surfaceMuted,
    border:          c.border,
    primary_text:    c.primaryText || c.text,
    secondary_text:  c.secondaryText || c.textMuted,
  };
  applyPosThemeVars(resolvePosTheme(effective), root);
}

export default POS_THEME;
